import React, { useEffect, useRef, useState } from 'react';
import { Typography, Stack } from '@mui/material';
import Withdraw from '../ClientWizard/Withdraw';
import RiderInfo from '../ClientWizard/RiderInfo';
import Steps from '../../common/cutom-stepper/Steps';
import ProductInfo from '../ClientWizard/ProductInfo';
import ProposalInfo from '../ClientWizard/ProposalInfo';
import StepItem from '../../common/cutom-stepper/StepItem';
import BaseInformation from '../ClientWizard/BaseInformation';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import DemographicInfo from '../ClientWizard/DemographicInfo';
import { useMutation } from '@tanstack/react-query';
import { CreateClient, CreateProposalInfo } from '../../../api/proposals';
import PreviewInfo from '../ClientWizard/PreviewInfo';
const renderDescriptionForStepOne = () => {
  return (
    <Stack alignItems="center" justifyContent="center">
      <Typography>Please complete required field to create a new client.</Typography>
      <Stack direction="row" spacing=".5em" alignItems="center" justifyContent="center">
        <NotificationsNoneOutlinedIcon color="primary" />
        <Typography color="primary" sx={{ padding: '.25rem 0' }}>
          This information is not editable after saving. You will need to delete and re-enter client
          information if changes are required.
        </Typography>
      </Stack>
    </Stack>
  );
};
const renderDescriptionForStepTwo = (info) => {
  return (
    <Stack spacing=".5rem" alignItems="center" justifyContent="center">
      <DemographicInfo data={info} />
    </Stack>
  );
};
/*
 * NewClientCreateStep is the wrapper component for the steps to create a new client. It will render all the steps. User can skip
 * any steps except the first step.
 * author: Kunle Badmus
 * */
let clientInfo = {};
export let initialIndex = 1;
const NewClientCreateSteps = ({
  handleModalClose,
  displayUpdate,
  isEditing,
  editableClient,
  initialStep,
  initialClient,
  versionList,
  oldVersionList,
  rowIndex,
  setIsClientCreatedSuccess,
}) => {
  const [initialKey, setInitialKey] = useState(initialIndex);
  const clientRef = useRef(null);
  const [info, setInfo] = useState({});
  const [clientError, setClientError] = useState();
  const [riderLoading, setRiderloading] = useState(false);
  useEffect(() => {
    initialIndex = initialStep;
    clientInfo = initialClient;
    setInitialKey(initialStep);
    setInfo(initialClient || {});
  }, [initialStep, initialClient]);
  const submitHandler = () => {
    const data = clientRef?.current?.getClientInfo();
    if (data) {
      storeClientInformation(data);
      setTimeout(() => {
        // setIsClientCreatedSuccess(true)
        createNewClient();
      }, 0);
    }
  };

  const storeClientInformation = (data) => {
    const newClient = {
      ...clientInfo,
      ...data,
    };
    clientInfo = newClient;
    setInfo(clientInfo);
  };

  const creationMethods = [CreateClient(info), CreateProposalInfo(info)];
  const addBaseInfo = useMutation({
    mutationFn: () => (initialKey === 1 ? creationMethods[0] : creationMethods[1])(),
  });

  const createNewClient = () => {
    if (initialIndex === 1 || initialIndex === 5) {
      addBaseInfo.mutate(
        {},
        {
          onError: (error) => {
            setClientError(error);
          },
          onSuccess: (data) => {
            clientInfo = { ...clientInfo, ...data };
            setInfo((prev) => ({ ...prev, ...data }));
            setInitialKey((prev) => prev + 1);
            initialIndex = Math.min(initialIndex + 1, 5);
            if (initialIndex === 5) {
              setInitialStep();
              displayUpdate(rowIndex || 0, clientInfo);
              setIsClientCreatedSuccess?.(true);
            } else {
              displayUpdate();
            }
          },
        }
      );
    } else {
      nextHandler();
    }
  };

  const nextHandler = () => {
    initialIndex = initialIndex < 5 && initialIndex + 1;
    setInitialKey((prev) => prev < 5 && prev + 1);
  };

  const prevHandler = () => {
    if (initialIndex === 5) {
      const data = clientRef?.current?.getClientInfo();
      if (data) {
        storeClientInformation(data);
      }
    }
    initialIndex = initialIndex - 1;
    setInitialKey(initialIndex);
  };

  const setInitialStep = () => {
    handleModalClose();
    initialIndex = 1;
    setInitialKey(1);
  };

  const client = { ...info };
  delete client['createdVersions'];
  return (
    <>
      <Steps
        riderLoading={riderLoading}
        initialKey={initialKey}
        handleCancel={setInitialStep}
        nextHandler={submitHandler}
        prevHandler={prevHandler}
        skipHandler={nextHandler}
      >
        <StepItem
          label="Create Client"
          description={renderDescriptionForStepOne()}
          error={
            addBaseInfo?.isError && 'Something went wrong to create client.Please try it again'
          }
          tabKey={1}
        >
          <BaseInformation
            ref={clientRef}
            client={client}
            isEditing={isEditing}
            editableClient={editableClient}
          />
        </StepItem>
        <StepItem
          label="Demographic and Proposal Information"
          tabKey={2}
          description={renderDescriptionForStepTwo(info)}
        >
          <ProposalInfo
            versionList={versionList}
            oldVersionList={oldVersionList}
            ref={clientRef}
            versions={info.createdVersions}
            client={client}
            isEditing={isEditing}
            editableClient={editableClient}
          />
        </StepItem>
        <StepItem
          label="Product specific"
          tabKey={3}
          description={
            <Typography>Please select required fields to add product specifics.</Typography>
          }
        >
          <ProductInfo
            ref={clientRef}
            data={info}
            versions={info.createdVersions}
            client={client}
          />
        </StepItem>
        <StepItem
          label="Rider Information"
          tabKey={4}
          description={
            <Typography> Please check each applicable box for desired rider(s).</Typography>
          }
        >
          <RiderInfo
            setRiderloading={setRiderloading}
            ref={clientRef}
            data={info}
            versions={info.createdVersions}
            client={client}
          />
        </StepItem>
        {/*<StepItem label="Add Withdrawals" tabKey={5} description={<Typography>Please verify that you have entered all applicable withdrawals amounts.</Typography>}  >*/}
        {/*    <Withdraw ref={clientRef} data={info} versions={info.createdVersions} client={client}/>*/}
        {/*</StepItem>*/}
        <StepItem
          label="Preview Mode"
          tabKey={5}
          description={<Typography>Review and click Finish to save this proposal.</Typography>}
        >
          <PreviewInfo ref={clientRef} data={info} versions={info.createdVersions} />
        </StepItem>
      </Steps>
    </>
  );
};

export default NewClientCreateSteps;
